/* Shared Types */
import { Application } from 'application/Shared/types/application';
import { FetchRecipeParams, Recipe } from 'application/Shared/types/recipe';

/* Hooks */
import { useFetchRecipes } from 'hooks/dataAccess/Recipe/recipe';
import { useFetchCustomerConnections } from 'hooks/dataAccess/Workato';

/* React */
import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react';

/* Local Types */
import { WorkatoActions, WorkatoConnection } from '../types';

type ApplicationsByProvider = Record<string, Application>;
type ConnectionsByProvider = Record<string, WorkatoConnection>;
type RecipesByProvider = Record<string, Recipe[]>;

interface ConnectionsAndRecipesContextType {
    applicationIds: string[];
    applicationsByProvider: ApplicationsByProvider;
    connectionsByProvider: ConnectionsByProvider;
    recipesByProvider: RecipesByProvider;
    connections: WorkatoConnection[];
    loading: boolean;
    recipes: Recipe[];
    refetchConnections: () => void;
}

interface RecipesAndAppsByProvider {
    applicationsByProviderLocal: ApplicationsByProvider;
    recipesByProviderLocal: RecipesByProvider;
}

export const ConnectionsAndRecipesContext =
    createContext<ConnectionsAndRecipesContextType>({
        applicationIds: [],
        applicationsByProvider: {},
        connections: [],
        connectionsByProvider: {},
        loading: true,
        recipes: [],
        recipesByProvider: {},
        refetchConnections: () => {}
    });

/**
 * Custom hook to access the ConnectionsAndRecipes context
 *
 * This hook provides access to the ConnectionsAndRecipes context which contains
 * information about Workato connections, recipes, and applications organized by provider.
 *
 * @throws {Error} If used outside of a ConnectionsAndRecipesContext.Provider
 * @returns {ConnectionsAndRecipesContextType} The connections and recipes context value
 */
export const useConnectionsAndRecipesContext = () => {
    const context = useContext(ConnectionsAndRecipesContext);

    if (context === undefined) {
        throw new Error(
            '`useConnectionsAndRecipesContext` must be used within `ConnectionsAndRecipesContext`'
        );
    }

    return context;
};

/**
 * Provider component for the ConnectionsAndRecipes context
 *
 * This component manages and provides access to Workato connections, recipes, and applications data
 * organized by provider. It handles fetching and organizing the data based on the provided recipe action
 * and application ID filters.
 *
 * @component
 * @param {Object} props
 * @param {WorkatoActions} [props.recipeAction] - Optional filter for recipes by action type (e.g. onboard/offboard)
 * @param {string | null} props.applicationId - Application ID(s) to filter recipes by. Can be comma-separated string for multiple IDs
 * @param {React.ReactNode} props.children - Child components that will have access to the context
 * @returns {JSX.Element} Provider component wrapping children with ConnectionsAndRecipes context
 */

export const ConnectionsAndRecipesContextProvider = ({
    clientId,
    recipeAction,
    applicationId,
    children
}: {
    clientId: string;
    recipeAction?: WorkatoActions;
    applicationId: string | null;
    children: React.ReactNode;
}) => {
    const [loading, setLoading] = useState(true);

    const [applicationsByProvider, setApplicationsByProvider] =
        useState<ApplicationsByProvider>({});

    const [recipesByProvider, setRecipesByProvider] =
        useState<RecipesByProvider>({});

    const [connectionsByProvider, setConnectionByProvider] =
        useState<ConnectionsByProvider>({});

    const appIdsArray = applicationId?.split(',');

    const recipeParams = useMemo(() => {
        const baseParams: FetchRecipeParams = {
            action: recipeAction,
            includeApplication: true,
            platformTenantId: clientId!,
            size: -1
        };

        // For now, we're going to load all recipes and not limit it to the selected apps in the URL.
        // In the state builder, we'll filter the recipes based on the selected apps.
        // Leaving this here for reference.
        // if (parsedApplicationIds?.length) {
        //     baseParams.applicationId = parsedApplicationIds;
        // }

        return baseParams;
    }, [recipeAction, clientId]);

    const { recipes, loading: loadingRecipes } = useFetchRecipes(recipeParams);

    // Fetch customer connections data based on the client ID
    const {
        connections,
        loading: loadingConnections,
        refetchData: refetchConnections
    }: {
        connections: WorkatoConnection[];
        loading: boolean;
        refetchData: () => void;
    } = useFetchCustomerConnections({
        platformTenantId: clientId!
    });

    useEffect(() => {
        if (loadingRecipes) {
            return;
        }

        const { recipesByProviderLocal, applicationsByProviderLocal } =
            recipes.reduce(
                (acc, recipe): RecipesAndAppsByProvider => {
                    const provider: string | undefined =
                        recipe.application?.workatoProvider;

                    if (!provider) {
                        return acc;
                    }

                    if (!acc.recipesByProviderLocal[provider!]) {
                        acc.recipesByProviderLocal[provider!] = [] as Recipe[];
                    }
                    acc.recipesByProviderLocal[provider!].push(recipe);

                    // We only need to assign the app once.
                    if (
                        recipe.application &&
                        !acc.applicationsByProviderLocal[provider!]
                    ) {
                        acc.applicationsByProviderLocal[provider!] =
                            recipe.application;
                    }

                    return acc;
                },
                {
                    applicationsByProviderLocal: {},
                    recipesByProviderLocal: {}
                } as RecipesAndAppsByProvider
            );

        setApplicationsByProvider(applicationsByProviderLocal);
        setRecipesByProvider(recipesByProviderLocal);

        setLoading(false);
    }, [recipes, loadingRecipes]);

    useEffect(() => {
        if (loadingConnections) {
            return;
        }

        const connectionsByProviderLocal = connections.reduce(
            (acc, connection): Record<string, WorkatoConnection> => {
                const provider: string | null | undefined =
                    connection?.provider;

                if (!provider) {
                    return acc;
                }

                // We only need to assign the connection once.
                if (!acc[provider!]) {
                    acc[provider!] = connection;
                }

                return acc;
            },
            {
                applicationsByProviderLocal: {},
                recipesByProviderLocal: {}
            } as Record<string, WorkatoConnection>
        );

        setConnectionByProvider(connectionsByProviderLocal);

        setLoading(false);
    }, [connections, loadingConnections]);

    const providerValues = useMemo(
        () => ({
            applicationIds: appIdsArray || [],
            applicationsByProvider,
            connections,
            connectionsByProvider,
            loading: loading || loadingConnections || loadingRecipes,
            recipes,
            recipesByProvider,
            refetchConnections
        }),
        [
            appIdsArray,
            applicationsByProvider,
            recipesByProvider,
            connectionsByProvider,
            connections,
            loading,
            loadingConnections,
            loadingRecipes,
            recipes,
            refetchConnections
        ]
    );

    return (
        <ConnectionsAndRecipesContext.Provider value={providerValues}>
            {children}
        </ConnectionsAndRecipesContext.Provider>
    );
};
